import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, AlertCircle, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const SalesAuditDashboard = ({ auditResult, onReset }) => {
  const [activeTab, setActiveTab] = useState('matched');

  if (!auditResult) {
    return (
      <Card className="glass-effect border-white/20">
        <CardContent className="p-8 text-center">
          <p className="text-white/70">Nenhum resultado disponível</p>
        </CardContent>
      </Card>
    );
  }

  const { matched, unmatchedERP, unmatchedOperator, summary } = auditResult;

  const formatMoney = (val) => {
    if (typeof val !== 'number') return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    if (dateStr.includes('/')) return dateStr;
    try {
      return new Date(dateStr + 'T12:00:00Z').toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const dataGeracao = new Date().toLocaleDateString('pt-BR');
    const horaGeracao = new Date().toLocaleTimeString('pt-BR');

    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text('Relatório de Auditoria de Vendas', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Gerado em: ${dataGeracao} às ${horaGeracao}`, 14, 28);

    // Resumo
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text('Resumo da Auditoria', 14, 40);

    doc.autoTable({
      startY: 45,
      head: [['Total ERP', 'Total Maquininhas', 'Encontrados', 'Sem Maquininha', 'Sem ERP']],
      body: [[
        `${formatMoney(summary.totalERP)} (${summary.countERP})`,
        `${formatMoney(summary.totalMaquininhas)} (${summary.countMaquininhas})`,
        `${formatMoney(summary.totalMatched)} (${summary.countMatched})`,
        `${formatMoney(summary.totalUnmatchedERP)} (${summary.countUnmatchedERP})`,
        `${formatMoney(summary.totalUnmatchedOp)} (${summary.countUnmatchedOp})`,
      ]],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      margin: { left: 14 },
    });

    let lastY = doc.lastAutoTable.finalY + 15;

    const checkPageBreak = (neededSpace) => {
      if (lastY + neededSpace > 280) {
        doc.addPage();
        lastY = 20;
      }
    };

    // Vendas Encontradas
    if (matched.length > 0) {
      checkPageBreak(25);
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Vendas Encontradas (Com Sucesso)', 14, lastY);

      doc.autoTable({
        startY: lastY + 5,
        head: [['Data', 'Valor', 'Forma Pagto', 'Maquininha', 'Bandeira', 'Status']],
        body: matched.map(item => [
          formatDate(item.erp.data),
          formatMoney(parseFloat(item.erp.valor) || 0),
          item.erp.formaPagamento || item.erp.bandeira || '—',
          item.maquininha.origin || '—',
          item.maquininha.bandeira || '—',
          'Encontrado',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] }, // Verde
        styles: { fontSize: 9 },
      });
      lastY = doc.lastAutoTable.finalY + 15;
    }

    // Sem Maquininha
    if (unmatchedERP.length > 0) {
      checkPageBreak(25);
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Vendas do Sistema (ERP) sem correspondente na Maquininha', 14, lastY);

      doc.autoTable({
        startY: lastY + 5,
        head: [['Data', 'Valor', 'Forma Pagto', 'Status']],
        body: unmatchedERP.map(item => [
          formatDate(item.erp.data),
          formatMoney(parseFloat(item.erp.valor) || 0),
          item.erp.formaPagamento || item.erp.bandeira || '—',
          'Sem maquininha',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [231, 76, 60] }, // Vermelho
        styles: { fontSize: 9 },
      });
      lastY = doc.lastAutoTable.finalY + 15;
    }

    // Sem ERP
    if (unmatchedOperator.length > 0) {
      checkPageBreak(25);
      doc.setFontSize(14);
      doc.setTextColor(40, 40, 40);
      doc.text('Vendas na Maquininha sem correspondente no Sistema (ERP)', 14, lastY);

      doc.autoTable({
        startY: lastY + 5,
        head: [['Data', 'Valor', 'Bandeira', 'Maquininha', 'Status']],
        body: unmatchedOperator.map(item => [
          formatDate(item.maquininha.data),
          formatMoney(item.maquininha.valor),
          item.maquininha.bandeira || '—',
          item.maquininha.origin || '—',
          'Sem ERP',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [243, 156, 18] }, // Laranja
        styles: { fontSize: 9 },
      });
      lastY = doc.lastAutoTable.finalY + 15;
    }

    doc.save(`auditoria_vendas_${dataGeracao.replace(/\//g, '-')}.pdf`);
  };

  const tabs = [
    { key: 'matched', label: 'Encontrados', count: summary.countMatched, color: 'text-green-400' },
    { key: 'unmatchedERP', label: 'Sem Maquininha', count: summary.countUnmatchedERP, color: 'text-red-400' },
    { key: 'unmatchedOp', label: 'Sem ERP', count: summary.countUnmatchedOp, color: 'text-orange-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-effect border-white/10 border-l-4 border-l-[#0084FF] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0084FF] shadow-[0_0_15px_#0084FF]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0084FF]/20 text-[#0084FF] flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 opacity-0" /> {/* Spacer to align visually, or just use a generic icon */}
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Total Sistema Interno</p>
              <p className="text-xl font-bold text-white">{formatMoney(summary.totalERP)}</p>
              <p className="text-xs text-white/50">{summary.countERP} vendas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-white/10 border-l-4 border-l-[#00D26A] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#00D26A] shadow-[0_0_15px_#00D26A]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#00D26A]/20 text-[#00D26A] flex-shrink-0">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Encontrados</p>
              <p className="text-xl font-bold text-[#00D26A]">{formatMoney(summary.totalMatched)}</p>
              <p className="text-xs text-white/50">{summary.countMatched} ({summary.percentMatched.toFixed(1)}%)</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-white/10 border-l-4 border-l-[#E11D48] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#E11D48] shadow-[0_0_15px_#E11D48]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#E11D48]/20 text-[#E11D48] flex-shrink-0">
              <XCircle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Sem Maquininha</p>
              <p className="text-xl font-bold text-[#E11D48]">{formatMoney(summary.totalUnmatchedERP)}</p>
              <p className="text-xs text-white/50">{summary.countUnmatchedERP} vendas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-effect border-white/10 border-l-4 border-l-[#F59E0B] relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#F59E0B] shadow-[0_0_15px_#F59E0B]"></div>
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[#F59E0B]/20 text-[#F59E0B] flex-shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="text-white/70 text-sm font-medium">Sem ERP</p>
              <p className="text-xl font-bold text-[#F59E0B]">{formatMoney(summary.totalUnmatchedOp)}</p>
              <p className="text-xs text-white/50">{summary.countUnmatchedOp} vendas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Status Geral */}
      <Card className={`glass-effect ${summary.bate100 ? 'border-green-500/50' : 'border-yellow-500/50'}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            {summary.bate100 ? (
              <>
                <CheckCircle2 className="w-8 h-8 text-green-400" />
                <div>
                  <h3 className="text-xl font-bold text-green-400">100% Auditoria Bem-Sucedida!</h3>
                  <p className="text-white/70 text-sm mt-1">
                    Todas as vendas do sistema interno foram encontradas nas maquininhas.
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="w-8 h-8 text-yellow-400" />
                <div>
                  <h3 className="text-xl font-bold text-yellow-400">Divergências Encontradas</h3>
                  <p className="text-white/70 text-sm mt-1">
                    {summary.countUnmatchedERP > 0 && `${summary.countUnmatchedERP} venda(s) do sistema sem maquininha. `}
                    {summary.countUnmatchedOp > 0 && `${summary.countUnmatchedOp} venda(s) na maquininha sem registro no sistema.`}
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs de navegação */}
      <div className="flex gap-1 bg-white/5 p-1 rounded-lg">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white/10 text-white'
                : 'text-white/50 hover:text-white/80'
            }`}
          >
            <span className={tab.color}>{tab.count}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Tabela de Resultados */}
      <Card className="glass-effect border-white/20">
        <CardContent className="pt-6">
          <div className="w-full">
            {activeTab === 'matched' && (
              <Table wrapperClassName="max-h-[500px] overflow-y-auto">
                <TableHeader>
                  <TableRow className="border-white/10 sticky top-0 z-10 bg-slate-900 shadow-md">
                    <TableHead className="text-white whitespace-nowrap">Data</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Valor</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Forma Pagto</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Maquininha</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Bandeira</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matched.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-white/50 py-8">
                        Nenhuma venda encontrada nas maquininhas
                      </TableCell>
                    </TableRow>
                  ) : (
                    matched.map((item, idx) => (
                      <TableRow key={idx} className="border-white/10">
                        <TableCell className="text-white/90 whitespace-nowrap">
                          {formatDate(item.erp.data)}
                        </TableCell>
                        <TableCell className="text-green-400 font-semibold whitespace-nowrap">
                          {formatMoney(parseFloat(item.erp.valor) || 0)}
                        </TableCell>
                        <TableCell className="text-sky-400 whitespace-nowrap">
                          {item.erp.formaPagamento || item.erp.bandeira || '—'}
                        </TableCell>
                        <TableCell className="text-purple-400 whitespace-nowrap">
                          {item.maquininha.origin || '—'}
                        </TableCell>
                        <TableCell className="text-amber-400 whitespace-nowrap">
                          {item.maquininha.bandeira || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs">
                            <CheckCircle2 className="w-3 h-3" />
                            Encontrado
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {activeTab === 'unmatchedERP' && (
              <Table wrapperClassName="max-h-[500px] overflow-y-auto">
                <TableHeader>
                  <TableRow className="border-white/10 sticky top-0 z-10 bg-slate-900 shadow-md">
                    <TableHead className="text-white whitespace-nowrap">Data</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Valor</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Forma Pagto</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unmatchedERP.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-white/50 py-8">
                        Todas as vendas do sistema foram encontradas nas maquininhas ✅
                      </TableCell>
                    </TableRow>
                  ) : (
                    unmatchedERP.map((item, idx) => (
                      <TableRow key={idx} className="border-white/10">
                        <TableCell className="text-white/90 whitespace-nowrap">
                          {formatDate(item.erp.data)}
                        </TableCell>
                        <TableCell className="text-white/90 font-semibold whitespace-nowrap">
                          {formatMoney(parseFloat(item.erp.valor) || 0)}
                        </TableCell>
                        <TableCell className="text-sky-400 whitespace-nowrap">
                          {item.erp.formaPagamento || item.erp.bandeira || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs">
                            <XCircle className="w-3 h-3" />
                            Sem maquininha
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {activeTab === 'unmatchedOp' && (
              <Table wrapperClassName="max-h-[500px] overflow-y-auto">
                <TableHeader>
                  <TableRow className="border-white/10 sticky top-0 z-10 bg-slate-900 shadow-md">
                    <TableHead className="text-white whitespace-nowrap">Data</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Valor</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Bandeira</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Maquininha</TableHead>
                    <TableHead className="text-white whitespace-nowrap">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unmatchedOperator.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-white/50 py-8">
                        Todas as vendas das maquininhas foram encontradas no sistema ✅
                      </TableCell>
                    </TableRow>
                  ) : (
                    unmatchedOperator.map((item, idx) => (
                      <TableRow key={idx} className="border-white/10">
                        <TableCell className="text-white/90 whitespace-nowrap">
                          {formatDate(item.maquininha.data)}
                        </TableCell>
                        <TableCell className="text-white/90 font-semibold whitespace-nowrap">
                          {formatMoney(item.maquininha.valor)}
                        </TableCell>
                        <TableCell className="text-amber-400 whitespace-nowrap">
                          {item.maquininha.bandeira || '—'}
                        </TableCell>
                        <TableCell className="text-purple-400 whitespace-nowrap">
                          {item.maquininha.origin || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 text-xs">
                            <AlertTriangle className="w-3 h-3" />
                            Sem ERP
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <div className="flex justify-center gap-4">
        <Button
          onClick={handleExportPDF}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Baixar PDF
        </Button>
        <Button
          onClick={onReset}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Nova Auditoria
        </Button>
      </div>
    </div>
  );
};

export default SalesAuditDashboard;
